import React, { useState, useEffect } from 'react';
import { Building2, Edit, Save, X, MapPin, Phone, Mail, Globe, MessageCircle, Users, HardDrive } from 'lucide-react';
import { useApiService } from '../../services/api';

const UserShowroom = () => {
  const { getUserShowroom, updateUserShowroom, getShowroomUsers } = useApiService();
  const [showroom, setShowroom] = useState(null);
  const [showroomUsers, setShowroomUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    showroomAddress: '',
    showroomPhone: '',
    showroomEmail: '',
    showroomWebsite: '',
    showroomDescription: '',
    whatsappNumber: '',
    facebookLink: '',
    instagramLink: '',
    twitterLink: '',
    linkedinLink: '',
    youtubeLink: '',
    googleMapLink: '',
    termsAndPolicy: '',
    logo: ''
  });

  useEffect(() => {
    loadUserShowroom();
    loadShowroomUsers();
  }, []);

  const loadUserShowroom = async () => {
    try {
      setLoading(true);
      const response = await getUserShowroom();
      setShowroom(response);
      setFormData({
        showroomAddress: response.showroomAddress || '',
        showroomPhone: response.showroomPhone || '',
        showroomEmail: response.showroomEmail || '',
        showroomWebsite: response.showroomWebsite || '',
        showroomDescription: response.showroomDescription || '',
        whatsappNumber: response.whatsappNumber || '',
        facebookLink: response.facebookLink || '',
        instagramLink: response.instagramLink || '',
        twitterLink: response.twitterLink || '',
        linkedinLink: response.linkedinLink || '',
        youtubeLink: response.youtubeLink || '',
        googleMapLink: response.googleMapLink || '',
        termsAndPolicy: response.policyAndTerms || '',
        logo: response.showroomLogo || ''
      });
      setError(null);
    } catch (err) {
      console.error('Error loading user showroom:', err);
      setError('Failed to load showroom information. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadShowroomUsers = async () => {
    try {
      setUsersLoading(true);
      const response = await getShowroomUsers();
      setShowroomUsers(response.users || []);
    } catch (err) {
      console.error('Error loading showroom users:', err);
    } finally {
      setUsersLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async () => {
    try {
      setSubmitting(true);
      await updateUserShowroom(formData);
      await loadUserShowroom();
      setEditing(false);
      alert('Showroom information updated successfully!');
    } catch (err) {
      console.error('Error updating showroom:', err);
      alert('Failed to update showroom information. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      showroomAddress: showroom?.showroomAddress || '',
      showroomPhone: showroom?.showroomPhone || '',
      showroomEmail: showroom?.showroomEmail || '',
      showroomWebsite: showroom?.showroomWebsite || '',
      showroomDescription: showroom?.showroomDescription || '',
      whatsappNumber: showroom?.whatsappNumber || '',
      facebookLink: showroom?.facebookLink || '',
      instagramLink: showroom?.instagramLink || '',
      twitterLink: showroom?.twitterLink || '',
      linkedinLink: showroom?.linkedinLink || '',
      youtubeLink: showroom?.youtubeLink || '',
      googleMapLink: showroom?.googleMapLink || '',
      termsAndPolicy: showroom?.policyAndTerms || '',
      logo: showroom?.showroomLogo || ''
    });
    setEditing(false);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif', background: '#fafbfc', minHeight: '100vh', padding: '2rem 1.5rem', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <div style={{ textAlign: 'center', color: '#6b7280' }}>Loading showroom information...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif', background: '#fafbfc', minHeight: '100vh', padding: '2rem 1.5rem' }}>
        <div className="card border-0 shadow-sm" style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '12px', padding: '1.5rem' }}>
          <div style={{ color: '#dc2626', fontSize: '0.875rem' }}>{error}</div>
        </div>
      </div>
    );
  }

  if (!showroom) {
    return (
      <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif', background: '#fafbfc', minHeight: '100vh', padding: '2rem 1.5rem' }}>
        <div style={{ textAlign: 'center', padding: '3rem' }}>
          <Building2 size={48} style={{ color: '#9ca3af', margin: '0 auto 1rem' }} />
          <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#1f2937', marginBottom: '0.5rem' }}>No showroom assigned</h3>
          <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>Please contact your administrator to be assigned to a showroom.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif', background: '#fafbfc', minHeight: '100vh', padding: '2rem 1.5rem', width: '100%', boxSizing: 'border-box' }}>
      {/* Header */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1.5rem' }}>
        <div>
          <h1 className="fw-bold mb-2" style={{ color: '#1f2937', fontSize: '1.75rem', letterSpacing: '-0.025em', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <Building2 size={28} style={{ color: '#6366f1' }} />
            My Showroom
          </h1>
          <p className="text-muted mb-0" style={{ fontSize: '0.875rem', color: '#6b7280' }}>Manage your showroom information and settings</p>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          {!editing ? (
            <button
              onClick={() => setEditing(true)}
              className="btn d-flex align-items-center justify-content-center gap-2 px-3 px-md-4 py-2 fw-semibold"
              style={{ 
                background: '#6366f1', 
                color: '#fff', 
                border: 'none', 
                borderRadius: '8px', 
                fontSize: '0.875rem',
                boxShadow: '0 1px 3px 0 rgba(99, 102, 241, 0.1), 0 1px 2px 0 rgba(99, 102, 241, 0.06)',
                cursor: 'pointer'
              }}
            >
              <Edit size={16} />
              Edit Showroom
            </button>
          ) : (
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              <button
                onClick={handleCancel}
                className="btn d-flex align-items-center justify-content-center gap-2 px-3 px-md-4 py-2 fw-semibold"
                style={{ 
                  background: '#fff', 
                  color: '#374151', 
                  border: '1px solid #d1d5db', 
                  borderRadius: '8px', 
                  fontSize: '0.875rem',
                  cursor: 'pointer'
                }}
              >
                <X size={16} />
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={submitting}
                className="btn d-flex align-items-center justify-content-center gap-2 px-3 px-md-4 py-2 fw-semibold"
                style={{ 
                  background: '#16a34a', 
                  color: '#fff', 
                  border: 'none', 
                  borderRadius: '8px', 
                  fontSize: '0.875rem',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  opacity: submitting ? 0.6 : 1,
                  boxShadow: '0 1px 3px 0 rgba(22, 163, 74, 0.1), 0 1px 2px 0 rgba(22, 163, 74, 0.06)'
                }}
              >
                <Save size={16} />
                {submitting ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem' }}>
        {/* Showroom Information */}
        <div style={{ gridColumn: 'span 2' }}>
          <div className="card border-0 shadow-sm" style={{ background: '#fff', borderRadius: '12px', padding: '1.5rem' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '2rem' }}>
              {/* Basic Information */}
              <div>
                <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#1f2937', marginBottom: '1rem' }}>Basic Information</h3>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>Showroom Name</label>
                    <div style={{ color: '#1f2937', fontWeight: '500', fontSize: '0.875rem' }}>{showroom.showroomName}</div>
                    <small style={{ color: '#6b7280', fontSize: '0.75rem' }}>This field cannot be edited</small>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>Address</label>
                    {editing ? (
                      <input
                        type="text"
                        name="showroomAddress"
                        value={formData.showroomAddress}
                        onChange={handleInputChange}
                        className="form-control"
                        style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.5rem 0.75rem', fontSize: '0.875rem' }}
                        placeholder="Enter showroom address"
                      />
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#1f2937', fontSize: '0.875rem' }}>
                        <MapPin size={16} style={{ color: '#9ca3af', flexShrink: 0 }} />
                        {showroom.showroomAddress || 'No address provided'}
                      </div>
                    )}
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>Phone</label>
                    {editing ? (
                      <input
                        type="tel"
                        name="showroomPhone"
                        value={formData.showroomPhone}
                        onChange={handleInputChange}
                        className="form-control"
                        style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.5rem 0.75rem', fontSize: '0.875rem' }}
                        placeholder="Enter phone number"
                      />
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#1f2937', fontSize: '0.875rem' }}>
                        <Phone size={16} style={{ color: '#9ca3af', flexShrink: 0 }} />
                        {showroom.showroomPhone || 'No phone provided'}
                      </div>
                    )}
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>Email</label>
                    {editing ? (
                      <input
                        type="email"
                        name="showroomEmail"
                        value={formData.showroomEmail}
                        onChange={handleInputChange}
                        className="form-control"
                        style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.5rem 0.75rem', fontSize: '0.875rem' }}
                        placeholder="Enter email address"
                      />
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#1f2937', fontSize: '0.875rem' }}>
                        <Mail size={16} style={{ color: '#9ca3af', flexShrink: 0 }} />
                        {showroom.showroomEmail || 'No email provided'}
                      </div>
                    )}
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>Website</label>
                    {editing ? (
                      <input
                        type="url"
                        name="showroomWebsite"
                        value={formData.showroomWebsite}
                        onChange={handleInputChange}
                        className="form-control"
                        style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.5rem 0.75rem', fontSize: '0.875rem' }}
                        placeholder="Enter website URL"
                      />
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#1f2937', fontSize: '0.875rem' }}>
                        <Globe size={16} style={{ color: '#9ca3af', flexShrink: 0 }} />
                        {showroom.showroomWebsite ? (
                          <a href={showroom.showroomWebsite} target="_blank" rel="noopener noreferrer" style={{ color: '#6366f1', textDecoration: 'none' }}>
                            {showroom.showroomWebsite}
                          </a>
                        ) : (
                          'No website provided'
                        )}
                      </div>
                    )}
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>WhatsApp Number</label>
                    {editing ? (
                      <input
                        type="tel"
                        name="whatsappNumber"
                        value={formData.whatsappNumber}
                        onChange={handleInputChange}
                        className="form-control"
                        style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.5rem 0.75rem', fontSize: '0.875rem' }}
                        placeholder="Enter WhatsApp number"
                      />
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#1f2937', fontSize: '0.875rem' }}>
                        <MessageCircle size={16} style={{ color: '#9ca3af', flexShrink: 0 }} />
                        {showroom.whatsappNumber || 'No WhatsApp number provided'}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Business Information */}
              <div>
                <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#1f2937', marginBottom: '1rem' }}>Business Information</h3>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>Description</label>
                    {editing ? (
                      <textarea
                        name="showroomDescription"
                        value={formData.showroomDescription}
                        onChange={handleInputChange}
                        rows={3}
                        className="form-control"
                        style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.5rem 0.75rem', fontSize: '0.875rem', resize: 'none' }}
                        placeholder="Enter showroom description"
                      />
                    ) : (
                      <div style={{ color: '#1f2937', fontSize: '0.875rem' }}>
                        {showroom.showroomDescription || 'No description provided'}
                      </div>
                    )}
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>Logo</label>
                    {editing ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '500', color: '#4b5563', marginBottom: '0.25rem' }}>Logo URL</label>
                          <input
                            type="url"
                            name="logo"
                            value={formData.logo}
                            onChange={handleInputChange}
                            className="form-control"
                            style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.5rem 0.75rem', fontSize: '0.875rem' }}
                            placeholder="Enter logo URL"
                          />
                        </div>
                        {formData.logo && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                            <img 
                              src={formData.logo} 
                              alt="Logo Preview" 
                              style={{ width: '64px', height: '64px', objectFit: 'contain', border: '1px solid #e5e7eb', borderRadius: '8px', background: '#fff' }}
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                            <div style={{ flex: 1 }}>
                              <p style={{ fontSize: '0.875rem', fontWeight: '500', color: '#1f2937', margin: 0 }}>Logo Preview</p>
                              <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: 0 }}>This is how your logo will appear</p>
                            </div>
                            <button
                              type="button"
                              onClick={() => setFormData(prev => ({ ...prev, logo: '' }))}
                              style={{ background: 'none', border: 'none', color: '#dc2626', fontSize: '0.875rem', fontWeight: '500', cursor: 'pointer' }}
                            >
                              Remove
                            </button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div>
                        {showroom.showroomLogo ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                            <img 
                              src={showroom.showroomLogo} 
                              alt="Showroom Logo" 
                              style={{ width: '64px', height: '64px', objectFit: 'contain', border: '1px solid #e5e7eb', borderRadius: '8px', background: '#fff' }}
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                            <div style={{ flex: 1 }}>
                              <p style={{ fontSize: '0.875rem', fontWeight: '500', color: '#1f2937', margin: 0 }}>Showroom Logo</p>
                              <a href={showroom.showroomLogo} target="_blank" rel="noopener noreferrer" style={{ color: '#6366f1', fontSize: '0.875rem', textDecoration: 'none' }}>
                                View Full Size
                              </a>
                            </div>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#6b7280', fontSize: '0.875rem' }}>
                            <div style={{ width: '64px', height: '64px', border: '2px dashed #d1d5db', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>No Logo</span>
                            </div>
                            <span>No logo provided</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>Terms & Policy</label>
                    {editing ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        <textarea
                          name="termsAndPolicy"
                          value={formData.termsAndPolicy}
                          onChange={handleInputChange}
                          rows={6}
                          maxLength={2000}
                          className="form-control"
                          style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.5rem 0.75rem', fontSize: '0.875rem', resize: 'none' }}
                          placeholder="Enter your showroom's terms and conditions..."
                        />
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.75rem', color: '#6b7280' }}>
                          <span>This information will be displayed to customers</span>
                          <span>{formData.termsAndPolicy.length}/2000 characters</span>
                        </div>
                      </div>
                    ) : (
                      <div style={{ color: '#1f2937', fontSize: '0.875rem' }}>
                        {showroom.policyAndTerms ? (
                          <div style={{ background: '#f9fafb', padding: '1rem', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                            <div style={{ fontSize: '0.875rem', whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>{showroom.policyAndTerms}</div>
                            <div style={{ marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid #e5e7eb' }}>
                              <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                                Last updated: {showroom.updatedAt ? new Date(showroom.updatedAt).toLocaleDateString() : 'Unknown'}
                              </span>
                            </div>
                          </div>
                        ) : (
                          <div style={{ textAlign: 'center', padding: '1.5rem', color: '#6b7280' }}>
                            <div style={{ width: '48px', height: '48px', margin: '0 auto 0.5rem', border: '2px dashed #d1d5db', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <span style={{ fontSize: '1rem' }}>📋</span>
                            </div>
                            <p style={{ fontSize: '0.875rem', margin: 0 }}>No terms and policy provided</p>
                            <p style={{ fontSize: '0.75rem', margin: '0.25rem 0 0 0' }}>Add your showroom's terms and conditions here</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Social Media & Additional Info */}
              <div>
                <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#1f2937', marginBottom: '1rem' }}>Social Media & Additional Information</h3>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>Google Maps Link</label>
                    {editing ? (
                      <input
                        type="url"
                        name="googleMapLink"
                        value={formData.googleMapLink}
                        onChange={handleInputChange}
                        className="form-control"
                        style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.5rem 0.75rem', fontSize: '0.875rem' }}
                        placeholder="Enter Google Maps link"
                      />
                    ) : (
                      <div style={{ color: '#1f2937', fontSize: '0.875rem' }}>
                        {showroom.googleMapLink ? (
                          <a href={showroom.googleMapLink} target="_blank" rel="noopener noreferrer" style={{ color: '#6366f1', textDecoration: 'none' }}>
                            View on Google Maps
                          </a>
                        ) : (
                          'No Google Maps link provided'
                        )}
                      </div>
                    )}
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>Facebook Link</label>
                    {editing ? (
                      <input
                        type="url"
                        name="facebookLink"
                        value={formData.facebookLink}
                        onChange={handleInputChange}
                        className="form-control"
                        style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.5rem 0.75rem', fontSize: '0.875rem' }}
                        placeholder="Enter Facebook link"
                      />
                    ) : (
                      <div style={{ color: '#1f2937', fontSize: '0.875rem' }}>
                        {showroom.facebookLink ? (
                          <a href={showroom.facebookLink} target="_blank" rel="noopener noreferrer" style={{ color: '#6366f1', textDecoration: 'none' }}>
                            Visit Facebook Page
                          </a>
                        ) : (
                          'No Facebook link provided'
                        )}
                      </div>
                    )}
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>Instagram Link</label>
                    {editing ? (
                      <input
                        type="url"
                        name="instagramLink"
                        value={formData.instagramLink}
                        onChange={handleInputChange}
                        className="form-control"
                        style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.5rem 0.75rem', fontSize: '0.875rem' }}
                        placeholder="Enter Instagram link"
                      />
                    ) : (
                      <div style={{ color: '#1f2937', fontSize: '0.875rem' }}>
                        {showroom.instagramLink ? (
                          <a href={showroom.instagramLink} target="_blank" rel="noopener noreferrer" style={{ color: '#6366f1', textDecoration: 'none' }}>
                            Visit Instagram Profile
                          </a>
                        ) : (
                          'No Instagram link provided'
                        )}
                      </div>
                    )}
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>Twitter Link</label>
                    {editing ? (
                      <input
                        type="url"
                        name="twitterLink"
                        value={formData.twitterLink}
                        onChange={handleInputChange}
                        className="form-control"
                        style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.5rem 0.75rem', fontSize: '0.875rem' }}
                        placeholder="Enter Twitter link"
                      />
                    ) : (
                      <div style={{ color: '#1f2937', fontSize: '0.875rem' }}>
                        {showroom.twitterLink ? (
                          <a href={showroom.twitterLink} target="_blank" rel="noopener noreferrer" style={{ color: '#6366f1', textDecoration: 'none' }}>
                            Visit Twitter Profile
                          </a>
                        ) : (
                          'No Twitter link provided'
                        )}
                      </div>
                    )}
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>LinkedIn Link</label>
                    {editing ? (
                      <input
                        type="url"
                        name="linkedinLink"
                        value={formData.linkedinLink}
                        onChange={handleInputChange}
                        className="form-control"
                        style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.5rem 0.75rem', fontSize: '0.875rem' }}
                        placeholder="Enter LinkedIn link"
                      />
                    ) : (
                      <div style={{ color: '#1f2937', fontSize: '0.875rem' }}>
                        {showroom.linkedinLink ? (
                          <a href={showroom.linkedinLink} target="_blank" rel="noopener noreferrer" style={{ color: '#6366f1', textDecoration: 'none' }}>
                            Visit LinkedIn Profile
                          </a>
                        ) : (
                          'No LinkedIn link provided'
                        )}
                      </div>
                    )}
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>YouTube Link</label>
                    {editing ? (
                      <input
                        type="url"
                        name="youtubeLink"
                        value={formData.youtubeLink}
                        onChange={handleInputChange}
                        className="form-control"
                        style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.5rem 0.75rem', fontSize: '0.875rem' }}
                        placeholder="Enter YouTube link"
                      />
                    ) : (
                      <div style={{ color: '#1f2937', fontSize: '0.875rem' }}>
                        {showroom.youtubeLink ? (
                          <a href={showroom.youtubeLink} target="_blank" rel="noopener noreferrer" style={{ color: '#6366f1', textDecoration: 'none' }}>
                            Visit YouTube Channel
                          </a>
                        ) : (
                          'No YouTube link provided'
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Storage Information */}
        <div>
          <div className="card border-0 shadow-sm" style={{ background: '#fff', borderRadius: '12px', padding: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
              <HardDrive size={20} style={{ color: '#6366f1' }} />
              <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#1f2937', margin: 0 }}>Storage Information</h3>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div style={{ padding: '1rem', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>Total Storage (MB)</span>
                  <span style={{ fontSize: '1rem', fontWeight: '600', color: '#1f2937' }}>
                    {showroom.storage !== undefined && showroom.storage !== null 
                      ? `${parseFloat(showroom.storage).toFixed(3)} MB`
                      : '20480.000 MB'}
                  </span>
                </div>
                <div style={{ 
                  width: '100%', 
                  height: '8px', 
                  background: '#e5e7eb', 
                  borderRadius: '4px', 
                  overflow: 'hidden',
                  marginTop: '0.5rem'
                }}>
                  <div style={{ 
                    width: '100%', 
                    height: '100%', 
                    background: '#6366f1',
                    borderRadius: '4px'
                  }}></div>
                </div>
              </div>

              <div style={{ padding: '1rem', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <span style={{ fontSize: '0.875rem', color: '#166534' }}>Storage Left (MB)</span>
                  <span style={{ fontSize: '1rem', fontWeight: '600', color: '#166534' }}>
                    {showroom.storageLeft !== undefined && showroom.storageLeft !== null 
                      ? `${parseFloat(showroom.storageLeft).toFixed(3)} MB`
                      : '20480.000 MB'}
                  </span>
                </div>
                <div style={{ 
                  width: '100%', 
                  height: '8px', 
                  background: '#dcfce7', 
                  borderRadius: '4px', 
                  overflow: 'hidden',
                  marginTop: '0.5rem'
                }}>
                  <div style={{ 
                    width: showroom.storage && showroom.storageLeft 
                      ? `${((parseFloat(showroom.storageLeft) / parseFloat(showroom.storage)) * 100).toFixed(1)}%`
                      : '100%', 
                    height: '100%', 
                    background: '#16a34a',
                    borderRadius: '4px',
                    transition: 'width 0.3s ease'
                  }}></div>
                </div>
                <div style={{ fontSize: '0.75rem', color: '#166534', marginTop: '0.5rem' }}>
                  {showroom.storage && showroom.storageLeft 
                    ? `${((parseFloat(showroom.storageLeft) / parseFloat(showroom.storage)) * 100).toFixed(1)}% available`
                    : '100% available'}
                </div>
              </div>

              <div style={{ padding: '0.75rem', background: '#fef3c7', borderRadius: '8px', border: '1px solid #fde68a' }}>
                <div style={{ fontSize: '0.75rem', color: '#92400e', lineHeight: '1.5' }}>
                  <strong>Note:</strong> Storage is automatically deducted when files are uploaded as attachments to deals.
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Showroom Users */}
        <div>
          <div className="card border-0 shadow-sm" style={{ background: '#fff', borderRadius: '12px', padding: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
              <Users size={20} style={{ color: '#6366f1' }} />
              <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#1f2937', margin: 0 }}>Showroom Users</h3>
            </div>
            
            {usersLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '128px' }}>
                <div style={{ textAlign: 'center', color: '#6b7280', fontSize: '0.875rem' }}>Loading users...</div>
              </div>
            ) : showroomUsers.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <Users size={32} style={{ color: '#9ca3af', margin: '0 auto 0.5rem' }} />
                <p style={{ color: '#6b7280', fontSize: '0.875rem', margin: 0 }}>No users found</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {showroomUsers.map((user) => (
                  <div key={user.id} style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.75rem' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: '500', color: '#1f2937', fontSize: '0.875rem', marginBottom: '0.25rem' }}>{user.name}</div>
                        <div style={{ fontSize: '0.875rem', color: '#4b5563', marginBottom: '0.5rem' }}>{user.email}</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                          <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            padding: '0.25rem 0.5rem',
                            borderRadius: '12px',
                            fontSize: '0.75rem',
                            fontWeight: '500',
                            background: user.role === 'admin' ? '#f3e8ff' : '#dbeafe',
                            color: user.role === 'admin' ? '#7c3aed' : '#1e40af'
                          }}>
                            {user.role}
                          </span>
                          <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            padding: '0.25rem 0.5rem',
                            borderRadius: '12px',
                            fontSize: '0.75rem',
                            fontWeight: '500',
                            background: user.status === 'active' ? '#d1fae5' : '#fee2e2',
                            color: user.status === 'active' ? '#065f46' : '#991b1b'
                          }}>
                            {user.status}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.5rem' }}>
                      Joined: {formatDate(user.createdAt)}
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #e5e7eb' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <div style={{ fontSize: '0.875rem', color: '#4b5563' }}>
                  Total Users: <span style={{ fontWeight: '500', color: '#1f2937' }}>{showroomUsers.length}</span>
                </div>
                {showroom.maxUsers !== undefined && showroom.maxUsers !== null && (
                  <div style={{ padding: '0.75rem', background: showroomUsers.length >= showroom.maxUsers ? '#fef2f2' : '#f0fdf4', borderRadius: '8px', border: `1px solid ${showroomUsers.length >= showroom.maxUsers ? '#fecaca' : '#bbf7d0'}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                      <span style={{ fontSize: '0.875rem', color: showroomUsers.length >= showroom.maxUsers ? '#991b1b' : '#166534', fontWeight: '500' }}>
                        User Limit
                      </span>
                      <span style={{ fontSize: '0.875rem', fontWeight: '600', color: showroomUsers.length >= showroom.maxUsers ? '#dc2626' : '#16a34a' }}>
                        {showroomUsers.length} / {showroom.maxUsers}
                      </span>
                    </div>
                    <div style={{ 
                      width: '100%', 
                      height: '8px', 
                      background: showroomUsers.length >= showroom.maxUsers ? '#fee2e2' : '#dcfce7', 
                      borderRadius: '4px', 
                      overflow: 'hidden',
                      marginTop: '0.5rem'
                    }}>
                      <div style={{ 
                        width: `${Math.min((showroomUsers.length / showroom.maxUsers) * 100, 100)}%`, 
                        height: '100%', 
                        background: showroomUsers.length >= showroom.maxUsers ? '#dc2626' : '#16a34a',
                        borderRadius: '4px',
                        transition: 'width 0.3s ease'
                      }}></div>
                    </div>
                    {showroomUsers.length >= showroom.maxUsers && (
                      <div style={{ fontSize: '0.75rem', color: '#991b1b', marginTop: '0.5rem' }}>
                        ⚠️ User limit reached. Cannot create more users.
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserShowroom;
